import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

/**
 * Brand tokens travel as an opaque record over the wire; the backend and the
 * frontend showcase validate them against the brand-token schemas in
 * @erp/json-renderer (the single source of truth for their shape).
 */
const BrandTokensWireSchema = z.record(z.string(), z.unknown());

export const BrandKitStatusSchema = z.enum(["draft", "published"]);

export const GetBrandKitInputSchema = z.object({});
export const GetBrandKitResponseSchema = z.object({
  brandKit: z
    .object({
      blockId: UUIDSchema,
      /** Tokens to display — the draft if one exists, else the published set. */
      tokens: BrandTokensWireSchema,
      status: BrandKitStatusSchema,
      hasDraft: z.boolean(),
      version: z.number().int(),
      /** Short-lived preview URLs keyed by logo documentId. */
      logoPreviewUrls: z.record(z.string(), z.string()),
      updatedAt: z.coerce.date(),
    })
    .nullable(),
});

export const PublishBrandKitInputSchema = z.object({});
export const PublishBrandKitResponseSchema = z.object({
  blockId: UUIDSchema,
  version: z.number().int(),
});

export const DiscardBrandKitDraftInputSchema = z.object({});
export const DiscardBrandKitDraftResponseSchema = z.object({
  discarded: z.boolean(),
  /** True when the whole (never-published) kit was removed. */
  reset: z.boolean(),
});

export const RenderBrandKitPreviewInputSchema = z.object({});
export const RenderBrandKitPreviewResponseSchema = z.object({
  html: z.string().nullable(),
  plainText: z.string().nullable(),
});

export const brandKitContracts = {
  get: oc
    .input(GetBrandKitInputSchema)
    .output(GetBrandKitResponseSchema)
    .route({
      method: "GET",
      summary: "Get the organization brand kit",
      description:
        "Returns the organization's brand kit tokens (colors, typography, logos) used for branded emails and documents, showing the draft if one exists, plus status, version, and logo preview links.",
    }),
  publish: oc
    .input(PublishBrandKitInputSchema)
    .output(PublishBrandKitResponseSchema)
    .route({
      method: "POST",
      summary: "Publish the brand kit draft",
      description:
        "Validates the brand kit draft is complete and publishes it as the organization's live brand. Fails listing missing sections.",
    }),
  discardDraft: oc
    .input(DiscardBrandKitDraftInputSchema)
    .output(DiscardBrandKitDraftResponseSchema)
    .route({
      method: "POST",
      summary: "Discard the brand kit draft",
      description:
        "Drops the working brand kit draft, keeping the published version live. A never-published kit is removed entirely.",
    }),
  renderPreview: oc
    .input(RenderBrandKitPreviewInputSchema)
    .output(RenderBrandKitPreviewResponseSchema)
    .route({
      method: "POST",
      summary: "Preview the brand kit",
      description:
        "Renders the current brand kit (draft or published) as a sample HTML and plain-text document with logos resolved, or null if none exists.",
    }),
} as const;
